import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { Avatar, Button, Empty, Input, List, Modal, Tag } from "antd";
import { UserOutlined } from "../icons";
import type { ScriptRoleReviewMemberSnapshot } from "../../../shared/scriptRoleReview";

const PROFILE_TAG_GROUPS: Array<{
  key:
    | "specificHealthTags"
    | "specificProductTags"
    | "specificFamilyTags"
    | "specificStoryTags"
    | "specificToneTags"
    | "forbiddenTopics";
  label: string;
  tone: "default" | "danger";
}> = [
  { key: "specificHealthTags", label: "健康", tone: "default" },
  { key: "specificProductTags", label: "产品", tone: "default" },
  { key: "specificFamilyTags", label: "家庭", tone: "default" },
  { key: "specificStoryTags", label: "故事", tone: "default" },
  { key: "specificToneTags", label: "语气", tone: "default" },
  { key: "forbiddenTopics", label: "禁忌", tone: "danger" },
];

const EMPTY_MEMBER_SELECTION_ISSUES: ReadonlyMap<string, string> = new Map();

export interface ScriptMemberPickerModalProps {
  open: boolean;
  tempRoleLabel: string;
  members: ScriptRoleReviewMemberSnapshot[];
  loading?: boolean;
  selectedWxid?: string;
  originalWxid?: string;
  memberSelectionIssueByWxid?: ReadonlyMap<string, string>;
  onCancel: () => void;
  onSelect: (wxid: string) => void;
}

function memberCategoryLabel(member: ScriptRoleReviewMemberSnapshot) {
  if (member.categoryCode && member.categoryName) {
    return `${member.categoryCode}-${member.categoryName}`;
  }
  return member.categoryName || member.category || "未分类";
}

function memberSearchText(member: ScriptRoleReviewMemberSnapshot) {
  return [
    member.number,
    member.nickname,
    member.wxid,
    member.deviceName,
    member.notes,
    member.category,
    member.categoryCode,
    member.categoryName,
    member.personaSummary,
    member.compactProfile,
    ...PROFILE_TAG_GROUPS.flatMap((group) => member[group.key]),
  ]
    .join("\n")
    .toLocaleLowerCase("zh-CN");
}

function compareMembers(
  left: ScriptRoleReviewMemberSnapshot,
  right: ScriptRoleReviewMemberSnapshot,
  selectedWxid?: string,
  originalWxid?: string
) {
  const priority = (wxid: string) => {
    if (wxid === selectedWxid) return 0;
    if (wxid === originalWxid) return 1;
    return 2;
  };
  const priorityDelta = priority(left.wxid) - priority(right.wxid);
  if (priorityDelta !== 0) return priorityDelta;
  return left.number.localeCompare(right.number, "zh-CN", { numeric: true });
}

export function ScriptMemberProfile(props: {
  member: ScriptRoleReviewMemberSnapshot;
  compact?: boolean;
  selectedLabel?: string;
}) {
  const { member, compact = false, selectedLabel } = props;
  const populatedTagGroups = PROFILE_TAG_GROUPS.filter((group) => member[group.key].length > 0);

  return (
    <div className={`script-review-member-profile${compact ? " is-compact" : ""}`}>
      <div className="script-review-member-identity">
        <Avatar
          size={compact ? 44 : 56}
          src={member.avatar || undefined}
          icon={<UserOutlined />}
          alt={member.nickname || member.number}
        />
        <div className="script-review-member-heading">
          <div className="script-review-member-name-line">
            <strong className="script-review-member-number">{member.number || "无编号"}</strong>
            <span className="script-review-member-name">{member.nickname || "未命名气氛员"}</span>
            {selectedLabel ? <Tag color="green">{selectedLabel}</Tag> : null}
          </div>
          <div className="script-review-member-meta">
            <span>{member.deviceName || "未填写设备"}</span>
            <span>{memberCategoryLabel(member)}</span>
            <span title={member.wxid}>{member.wxid}</span>
          </div>
        </div>
      </div>

      <div className="script-review-profile-grid">
        <section className="script-review-profile-section">
          <span className="script-review-profile-label">人设</span>
          <p>{member.personaSummary || "未提取人设摘要"}</p>
          {member.compactProfile ? (
            <p className="script-review-compact-profile">{member.compactProfile}</p>
          ) : null}
        </section>
        <section className="script-review-profile-section">
          <span className="script-review-profile-label">原始备注</span>
          <p>{member.notes || "未填写备注"}</p>
        </section>
      </div>

      <div className="script-review-tag-groups">
        {populatedTagGroups.length > 0 ? (
          populatedTagGroups.map((group) => (
            <div className="script-review-tag-group" key={group.key}>
              <span>{group.label}</span>
              <div>
                {member[group.key].map((tag, index) => (
                  <Tag
                    key={`${group.key}-${index}-${tag}`}
                    className="script-review-profile-tag"
                    color={group.tone === "danger" ? "red" : undefined}
                    variant="filled"
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            </div>
          ))
        ) : (
          <span className="script-review-profile-empty">暂无画像标签</span>
        )}
      </div>
    </div>
  );
}

export function ScriptMemberPickerModal({
  open,
  tempRoleLabel,
  members,
  loading = false,
  selectedWxid,
  originalWxid,
  memberSelectionIssueByWxid = EMPTY_MEMBER_SELECTION_ISSUES,
  onCancel,
  onSelect,
}: ScriptMemberPickerModalProps) {
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query.trim().toLocaleLowerCase("zh-CN"));

  useEffect(() => {
    if (open) setQuery("");
  }, [open, tempRoleLabel]);

  const visibleMembers = useMemo(() => {
    const filtered = deferredQuery
      ? members.filter((member) => memberSearchText(member).includes(deferredQuery))
      : members;
    return [...filtered].sort((left, right) =>
      compareMembers(left, right, selectedWxid, originalWxid)
    );
  }, [deferredQuery, members, originalWxid, selectedWxid]);

  return (
    <Modal
      title={`为${tempRoleLabel}选择气氛员`}
      open={open}
      width="min(960px, 94vw)"
      style={{ top: "6vh", paddingBottom: 0 }}
      footer={<Button onClick={onCancel}>取消</Button>}
      onCancel={onCancel}
      destroyOnHidden
      mask={{ closable: false, blur: true }}
      styles={{
        container: {
          height: "min(86vh, 820px)",
          padding: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
        header: { margin: 0, padding: "16px 20px", borderBottom: "1px solid #e2e8f0" },
        body: { minHeight: 0, flex: 1, padding: 0, overflow: "hidden" },
        footer: { margin: 0, padding: "12px 20px", borderTop: "1px solid #e2e8f0" },
      }}
    >
      <div className="script-member-picker">
        <div className="script-member-picker-toolbar">
          <Input
            allowClear
            autoFocus
            aria-label="搜索气氛员"
            size="large"
            value={query}
            placeholder="搜索编号、昵称、设备、分类、备注或画像标签"
            onChange={(event) => setQuery(event.target.value)}
          />
          <span role="status" aria-live="polite">
            {deferredQuery ? `找到 ${visibleMembers.length} / ${members.length} 人` : `共 ${members.length} 人`}
          </span>
        </div>

        <div className="script-member-picker-list">
          <List
            loading={loading}
            rowKey="wxid"
            split={false}
            dataSource={visibleMembers}
            locale={{
              emptyText: (
                <Empty
                  description={loading ? "正在加载气氛员资料" : "没有符合条件的气氛员"}
                />
              ),
            }}
            renderItem={(member) => {
              const isSelected = member.wxid === selectedWxid;
              const isOriginal = member.wxid === originalWxid;
              const selectionIssue = memberSelectionIssueByWxid.get(member.wxid);
              return (
                <List.Item>
                  <div
                    role="button"
                    tabIndex={selectionIssue ? -1 : 0}
                    className={`script-member-option${isSelected ? " is-selected" : ""}${selectionIssue ? " is-disabled" : ""}`}
                    aria-pressed={isSelected}
                    aria-disabled={Boolean(selectionIssue)}
                    onClick={() => {
                      if (!selectionIssue) onSelect(member.wxid);
                    }}
                    onKeyDown={(event) => {
                      if (selectionIssue) return;
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      onSelect(member.wxid);
                    }}
                  >
                    {isSelected || isOriginal || selectionIssue ? (
                      <div className="script-member-option-status">
                        {isSelected ? <Tag className="script-member-status-tag" color="green">当前选择</Tag> : null}
                        {isOriginal ? <Tag className="script-member-status-tag">AI 原匹配</Tag> : null}
                        {selectionIssue ? <Tag className="script-member-status-tag" color="red">{selectionIssue}</Tag> : null}
                      </div>
                    ) : null}
                    <ScriptMemberProfile member={member} compact />
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      </div>
    </Modal>
  );
}
